import { useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import { QUERY_WIDTHS } from "@utils/index";
import { NotificationModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useNotificationReadAll,
  useNotificationReadOne,
} from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  BellRingingSvg,
  ChecksSvg,
  NotificationItem,
} from "@components/index";

export const NotificationsBar = ({
  notifications,
  onClose,
}: {
  notifications: NotificationModel[];
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const setState = useCommonStore(({ setState }) => setState);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const queryClient = useQueryClient();
  const notify = useNotify();

  const { mutate: mutateReadAll, isPending: isPendingReadAll } =
    useNotificationReadAll(
      () => queryClient.invalidateQueries({ queryKey: ["notificationGetAll"] }),
      ({ message }) => notify("error", message),
    );

  const { mutate: mutateReadOne, isPending: isPendingReadOne } =
    useNotificationReadOne(
      () => queryClient.invalidateQueries({ queryKey: ["notificationGetAll"] }),
      ({ message }) => notify("error", message),
    );

  useEffect(() => {
    setState({ isLoading: isPendingReadAll || isPendingReadOne });
  }, [isPendingReadAll, isPendingReadOne, setState]);

  const importantItems = notifications.filter(
    ({ isImportant, readAt }) => isImportant && !readAt,
  );
  const unreadItems = notifications.filter(
    ({ isImportant, readAt }) => !isImportant && !readAt,
  );
  const readItems = notifications.filter(({ readAt }) => readAt);

  const handleClose = () => {
    setState({ isDrawerShown: false });
    onClose();
  };

  return (
    <Drawer className="md:w-[468px]">
      <DrawerHeader
        onClose={handleClose}
        title={
          <div className="flex items-center gap-x-3 text-300 font-bold text-black-100 md:text-500">
            {!isMobile && (
              <div className="*:h-8 *:w-8">
                <BellRingingSvg />
              </div>
            )}
            Notification center
          </div>
        }
        className="md:px-8"
        classNameCloseButton="-left-[49px]"
      />
      <DrawerScrollableContainer className="md:px-8">
        <div className="pt-4 md:pt-6">
          {!importantItems.length ? null : (
            <div className="flex flex-col gap-y-4">
              <div className="border-b border-dashed border-b-black-24 py-3 text-300 font-bold text-black-100">
                Outstanding Notifications:
              </div>

              {importantItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  hideAssetItem={disabledFeatures.includes("Assets")}
                  onClose={handleClose}
                />
              ))}
            </div>
          )}

          {!unreadItems.length ? null : (
            <div
              className={clsx(
                "flex flex-col gap-y-4",
                !!importantItems.length && "pt-8 md:pt-11",
              )}
            >
              <div className="flex justify-between border-b border-dashed border-b-black-24 py-3">
                <div className="text-300 font-bold text-black-100">
                  Unread Notifications:
                </div>
                <button
                  type="button"
                  className="flex items-center gap-x-2 font-bold text-primary-300"
                  onClick={() => mutateReadAll()}
                >
                  <ChecksSvg />
                  <div>Mark all as read</div>
                </button>
              </div>

              {unreadItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  hideAssetItem={disabledFeatures.includes("Assets")}
                  readOne={() => mutateReadOne(notification.id)}
                  onClose={handleClose}
                />
              ))}
            </div>
          )}

          {!readItems.length ? null : (
            <div
              className={clsx(
                "flex flex-col gap-y-4",
                !!unreadItems.length && "pt-8 md:pt-11",
              )}
            >
              <div className="border-b border-dashed border-b-black-24 py-3 text-300 font-bold text-black-70">
                Read Notifications:
              </div>

              {readItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  hideAssetItem={disabledFeatures.includes("Assets")}
                  onClose={handleClose}
                />
              ))}
            </div>
          )}
        </div>
      </DrawerScrollableContainer>
    </Drawer>
  );
};
